// F9 — the verified layer of the food database.
//
// OpenFoodFacts gives us unbeatable *packaged* breadth, but it is crowdsourced
// and weakest exactly where people log most: plain whole foods ("chicken
// breast", "Magerquark", "Haferflocken"). This curated set covers those, is
// bundled into the app (so generic search works OFFLINE), and is marked
// `verified` in the UI so users can see which numbers are reference data and
// which are community entries.
//
// Values are per 100 g (or 100 ml where noted) rounded reference figures for
// the raw/plain form, based on public-domain composition data (USDA
// FoodData Central SR Legacy) cross-checked against German staples. They are
// reference values, not brand-specific — a brand's own label always wins, and
// barcode scanning still routes to OFF.

export interface VerifiedFood {
  id: string;
  /** Display names — matched case/diacritic-insensitively in both locales. */
  en: string;
  de: string;
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  sugar_g?: number;
  unit: "g" | "ml";
}

// Compact tuple form keeps the bundle small; expanded once at module load.
type Row = [string, string, string, number, number, number, number, number?, ("g" | "ml")?];

const ROWS: Row[] = [
  // ── Poultry, meat, fish ────────────────────────────────────────────────
  ["chicken-breast", "Chicken breast, raw", "Hähnchenbrust, roh", 120, 23, 0, 2.6],
  ["chicken-thigh", "Chicken thigh, raw", "Hähnchenschenkel, roh", 177, 19, 0, 11],
  ["turkey-breast", "Turkey breast, raw", "Putenbrust, roh", 111, 24, 0, 1.2],
  ["beef-mince-5", "Beef mince, 5% fat, raw", "Rinderhackfleisch, 5% Fett, roh", 137, 21, 0, 5],
  ["beef-mince-20", "Beef mince, 20% fat, raw", "Rinderhackfleisch, 20% Fett, roh", 254, 17, 0, 20],
  ["beef-steak", "Beef steak, lean, raw", "Rindersteak, mager, roh", 143, 22, 0, 6],
  ["pork-schnitzel", "Pork loin, raw", "Schweineschnitzel, roh", 143, 22, 0, 6],
  ["pork-mince", "Pork mince, raw", "Schweinehackfleisch, roh", 263, 17, 0, 21],
  ["bacon", "Bacon, raw", "Speck, roh", 393, 12, 1.3, 37],
  ["salami", "Salami", "Salami", 407, 22, 1.2, 35],
  ["ham-cooked", "Cooked ham", "Kochschinken", 107, 18, 1, 3.5],
  ["salmon", "Salmon, raw", "Lachs, roh", 208, 20, 0, 13],
  ["cod", "Cod, raw", "Kabeljau, roh", 82, 18, 0, 0.7],
  ["tuna-can", "Tuna in water, drained", "Thunfisch in Wasser, abgetropft", 116, 26, 0, 1],
  ["shrimp", "Shrimp, raw", "Garnelen, roh", 85, 20, 0, 0.5],
  ["trout", "Trout, raw", "Forelle, roh", 119, 20, 0, 4],

  // ── Eggs & dairy ───────────────────────────────────────────────────────
  ["egg", "Egg, whole, raw", "Ei, ganz, roh", 143, 13, 0.7, 9.5, 0.4],
  ["egg-white", "Egg white", "Eiklar", 52, 11, 0.7, 0.2],
  ["milk-35", "Milk, 3.5%", "Vollmilch, 3,5%", 64, 3.4, 4.8, 3.5, 4.8, "ml"],
  ["milk-15", "Milk, 1.5%", "Milch, 1,5%", 47, 3.4, 4.9, 1.5, 4.9, "ml"],
  ["quark-lean", "Quark, low fat", "Magerquark", 67, 12, 4.1, 0.3, 4.1],
  ["quark-20", "Quark, 20% fat", "Speisequark, 20%", 109, 12, 3.2, 5.1, 3.2],
  ["skyr", "Skyr, plain", "Skyr, natur", 63, 11, 4, 0.2, 4],
  ["greek-yogurt", "Greek yogurt, 10%", "Griechischer Joghurt, 10%", 133, 5.5, 3.6, 10, 3.6],
  ["yogurt-plain", "Yogurt, plain 3.5%", "Naturjoghurt, 3,5%", 63, 3.5, 4.7, 3.5, 4.7],
  ["cottage-cheese", "Cottage cheese", "Hüttenkäse", 98, 12, 3.4, 4.3, 3.4],
  ["cream-cheese", "Cream cheese", "Frischkäse", 253, 6, 4, 24, 3.5],
  ["gouda", "Gouda", "Gouda", 356, 25, 2.2, 27],
  ["emmental", "Emmental", "Emmentaler", 380, 28, 1.4, 30],
  ["mozzarella", "Mozzarella", "Mozzarella", 254, 18, 2.2, 19],
  ["feta", "Feta", "Feta", 264, 14, 4.1, 21],
  ["parmesan", "Parmesan", "Parmesan", 392, 36, 3.2, 26],
  ["butter", "Butter", "Butter", 717, 0.9, 0.1, 81],
  ["cream-30", "Cream, 30%", "Schlagsahne, 30%", 292, 2.4, 3.1, 30, 3.1, "ml"],
  ["schmand", "Sour cream, 24%", "Schmand, 24%", 237, 2.8, 3.2, 24, 3.2],

  // ── Grains, bread, pasta ───────────────────────────────────────────────
  ["oats", "Oats, rolled", "Haferflocken", 372, 13, 59, 7, 1.2],
  ["rice-white-raw", "White rice, raw", "Reis, weiß, roh", 351, 7, 78, 0.6],
  ["rice-white-cooked", "White rice, cooked", "Reis, weiß, gekocht", 130, 2.7, 28, 0.3],
  ["rice-brown-raw", "Brown rice, raw", "Naturreis, roh", 353, 7.5, 74, 2.7],
  ["pasta-raw", "Pasta, dry", "Nudeln, roh", 359, 12, 71, 1.5],
  ["pasta-cooked", "Pasta, cooked", "Nudeln, gekocht", 158, 5.8, 31, 0.9],
  ["potato-raw", "Potato, raw", "Kartoffel, roh", 77, 2, 17, 0.1],
  ["potato-boiled", "Potato, boiled", "Kartoffel, gekocht", 87, 1.9, 20, 0.1],
  ["sweet-potato", "Sweet potato, raw", "Süßkartoffel, roh", 86, 1.6, 20, 0.1, 4.2],
  ["bread-wholegrain", "Wholegrain bread", "Vollkornbrot", 232, 8.5, 39, 3.5, 3],
  ["bread-rye", "Rye bread", "Roggenbrot", 216, 6.5, 42, 1.2, 2],
  ["bread-toast", "Toast bread", "Toastbrot", 265, 8, 48, 3.8, 4],
  ["broetchen", "Bread roll", "Brötchen", 264, 8.6, 52, 1.4],
  ["crispbread", "Crispbread", "Knäckebrot", 350, 10, 66, 2.5],
  ["couscous-raw", "Couscous, dry", "Couscous, roh", 376, 13, 77, 0.6],
  ["quinoa-raw", "Quinoa, dry", "Quinoa, roh", 368, 14, 64, 6],
  ["bulgur-raw", "Bulgur, dry", "Bulgur, roh", 342, 12, 76, 1.3],
  ["muesli", "Muesli, unsweetened", "Müsli, ungesüßt", 367, 10, 60, 8, 12],

  // ── Legumes & plant protein ────────────────────────────────────────────
  ["lentils-raw", "Lentils, dry", "Linsen, roh", 353, 25, 60, 1.1],
  ["lentils-cooked", "Lentils, cooked", "Linsen, gekocht", 116, 9, 20, 0.4],
  ["chickpeas-can", "Chickpeas, drained", "Kichererbsen, abgetropft", 139, 7.5, 20, 2.6],
  ["kidney-beans-can", "Kidney beans, drained", "Kidneybohnen, abgetropft", 127, 8.5, 19, 0.5],
  ["tofu", "Tofu, plain", "Tofu, natur", 122, 13, 1.9, 7],
  ["tempeh", "Tempeh", "Tempeh", 192, 20, 8, 11],
  ["soy-milk", "Soy drink, unsweetened", "Sojadrink, ungesüßt", 33, 3.3, 0.6, 1.8, 0.3, "ml"],

  // ── Vegetables ─────────────────────────────────────────────────────────
  ["broccoli", "Broccoli, raw", "Brokkoli, roh", 34, 2.8, 7, 0.4, 1.7],
  ["carrot", "Carrot, raw", "Karotte, roh", 41, 0.9, 10, 0.2, 4.7],
  ["cucumber", "Cucumber", "Gurke", 15, 0.7, 3.6, 0.1, 1.7],
  ["tomato", "Tomato", "Tomate", 18, 0.9, 3.9, 0.2, 2.6],
  ["bell-pepper", "Bell pepper", "Paprika", 31, 1, 6, 0.3, 4.2],
  ["onion", "Onion", "Zwiebel", 40, 1.1, 9.3, 0.1, 4.2],
  ["spinach", "Spinach, raw", "Spinat, roh", 23, 2.9, 3.6, 0.4, 0.4],
  ["zucchini", "Zucchini", "Zucchini", 17, 1.2, 3.1, 0.3, 2.5],
  ["cauliflower", "Cauliflower, raw", "Blumenkohl, roh", 25, 1.9, 5, 0.3, 1.9],
  ["mushrooms", "Mushrooms, raw", "Champignons, roh", 22, 3.1, 3.3, 0.3, 2],
  ["lettuce", "Lettuce", "Kopfsalat", 15, 1.4, 2.9, 0.2, 0.8],
  ["sauerkraut", "Sauerkraut", "Sauerkraut", 19, 0.9, 4.3, 0.1, 1.8],
  ["peas-frozen", "Peas, frozen", "Erbsen, tiefgekühlt", 81, 5.4, 14, 0.4, 5],
  ["corn-can", "Sweetcorn, drained", "Mais, abgetropft", 86, 3.2, 19, 1.2, 6.3],

  // ── Fruit ──────────────────────────────────────────────────────────────
  ["apple", "Apple", "Apfel", 52, 0.3, 14, 0.2, 10],
  ["banana", "Banana", "Banane", 89, 1.1, 23, 0.3, 12],
  ["orange", "Orange", "Orange", 47, 0.9, 12, 0.1, 9],
  ["strawberries", "Strawberries", "Erdbeeren", 32, 0.7, 7.7, 0.3, 4.9],
  ["blueberries", "Blueberries", "Heidelbeeren", 57, 0.7, 14, 0.3, 10],
  ["raspberries", "Raspberries", "Himbeeren", 52, 1.2, 12, 0.7, 4.4],
  ["grapes", "Grapes", "Weintrauben", 69, 0.7, 18, 0.2, 16],
  ["watermelon", "Watermelon", "Wassermelone", 30, 0.6, 7.6, 0.2, 6.2],
  ["pineapple", "Pineapple", "Ananas", 50, 0.5, 13, 0.1, 10],
  ["mango", "Mango", "Mango", 60, 0.8, 15, 0.4, 14],
  ["avocado", "Avocado", "Avocado", 160, 2, 8.5, 15, 0.7],
  ["pear", "Pear", "Birne", 57, 0.4, 15, 0.1, 10],
  ["kiwi", "Kiwi", "Kiwi", 61, 1.1, 15, 0.5, 9],

  // ── Fats, nuts, seeds ──────────────────────────────────────────────────
  ["olive-oil", "Olive oil", "Olivenöl", 884, 0, 0, 100, 0, "ml"],
  ["rapeseed-oil", "Rapeseed oil", "Rapsöl", 884, 0, 0, 100, 0, "ml"],
  ["almonds", "Almonds", "Mandeln", 579, 21, 22, 50, 4.4],
  ["walnuts", "Walnuts", "Walnüsse", 654, 15, 14, 65, 2.6],
  ["cashews", "Cashews", "Cashewkerne", 553, 18, 30, 44, 5.9],
  ["peanuts", "Peanuts", "Erdnüsse", 567, 26, 16, 49, 4],
  ["peanut-butter", "Peanut butter", "Erdnussbutter", 588, 25, 20, 50, 9],
  ["sunflower-seeds", "Sunflower seeds", "Sonnenblumenkerne", 584, 21, 20, 51, 2.6],
  ["chia", "Chia seeds", "Chiasamen", 486, 17, 42, 31, 0],
  ["flaxseed", "Flaxseed", "Leinsamen", 534, 18, 29, 42, 1.6],

  // ── Staples & extras ───────────────────────────────────────────────────
  ["honey", "Honey", "Honig", 304, 0.3, 82, 0, 82],
  ["sugar", "Sugar", "Zucker", 387, 0, 100, 0, 100],
  ["dark-chocolate", "Dark chocolate, 70%", "Zartbitterschokolade, 70%", 598, 7.8, 46, 43, 24],
  ["milk-chocolate", "Milk chocolate", "Vollmilchschokolade", 535, 7.6, 59, 30, 52],
  ["whey-protein", "Whey protein powder", "Whey-Proteinpulver", 380, 80, 6, 4, 4],
  ["ketchup", "Ketchup", "Ketchup", 112, 1.2, 26, 0.1, 22],
  ["mayonnaise", "Mayonnaise", "Mayonnaise", 680, 1, 1.5, 75],
  ["mustard", "Mustard", "Senf", 66, 4, 5, 3.3, 1],
  ["beer", "Beer, 5%", "Bier, 5%", 43, 0.5, 3.6, 0, 0, "ml"],
  ["wine-red", "Red wine", "Rotwein", 85, 0.1, 2.6, 0, 0.6, "ml"],
];

export const VERIFIED_FOODS: VerifiedFood[] = ROWS.map(
  ([id, en, de, kcal, protein_g, carbs_g, fat_g, sugar_g, unit]) => ({
    id, en, de, kcal, protein_g, carbs_g, fat_g,
    sugar_g,
    unit: unit ?? "g",
  }),
);

// German users type umlauts three ways: "Süßkartoffel", "Susskartoffel"
// (diacritic dropped) and "Suesskartoffel" (transliterated). Both folds are
// indexed so all three spellings hit.
function base(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9äöüß ]/gi, " ");
}
function tidy(s: string): string {
  return s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
}
/** ü → u (plain diacritic strip). */
function foldPlain(s: string): string {
  return tidy(base(s).replace(/ß/g, "ss"));
}
/** ü → ue (German transliteration). */
function foldDe(s: string): string {
  return tidy(
    base(s)
      .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss"),
  );
}

const INDEX = VERIFIED_FOODS.map((f) => ({
  food: f,
  hay: `${foldPlain(f.en)} ${foldPlain(f.de)} ${foldDe(f.de)}`,
}));

/** Verified matches for a query, best first. Prefix hits rank above contains. */
export function searchVerified(query: string, limit = 8): VerifiedFood[] {
  const forms = [foldPlain(query), foldDe(query)].filter((q, i, a) => q.length >= 2 && a.indexOf(q) === i);
  if (forms.length === 0) return [];
  const scored: Array<{ f: VerifiedFood; score: number }> = [];
  for (const { food, hay } of INDEX) {
    let best = Infinity;
    for (const q of forms) {
      const at = hay.indexOf(q);
      if (at === -1) continue;
      // Word-start matches beat mid-word ones; earlier matches beat later.
      const wordStart = at === 0 || hay[at - 1] === " ";
      best = Math.min(best, (wordStart ? 0 : 1000) + at);
    }
    if (best !== Infinity) scored.push({ f: food, score: best });
  }
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limit).map((s) => s.f);
}

/** Adapts a verified food to the product shape the logging sheets already
 *  speak. The `verified:` code is deliberately non-numeric so normalizeCode()
 *  rejects it and it can never be written into the barcode cache. */
export function verifiedToProduct(f: VerifiedFood, locale: string): {
  code: string; name: string; brand: string; image_url: null;
  unit: "g" | "ml"; serving_size_g: null;
  per_100g: { kcal: number; protein_g: number; carbs_g: number; fat_g: number; sugar_g: number | null };
} {
  return {
    code: `verified:${f.id}`,
    name: locale === "de" ? f.de : f.en,
    brand: "",
    image_url: null,
    unit: f.unit,
    serving_size_g: null,
    per_100g: {
      kcal: f.kcal, protein_g: f.protein_g, carbs_g: f.carbs_g,
      fat_g: f.fat_g, sugar_g: f.sugar_g ?? null,
    },
  };
}
